const frame = ({
  id,
  settingId,
  title,
  roles,
  tones,
  abilityTags,
  incident,
  duty,
  pressures,
  optionA,
  optionB,
  stakes,
}) => ({
  id,
  settingId,
  title,
  roles,
  tones,
  abilityTags,
  incident,
  duty,
  pressures,
  optionA,
  optionB,
  stakes,
});

// A frame is one authored causal chain. Only details inside `pressures` are
// interchangeable; the incident, duty, decision, and stakes stay connected.
export const scenarioFrames = [
  frame({
    id: 'canal-gate-jam',
    settingId: 'scadrial-canal-district',
    title: 'The Gate Will Not Open',
    roles: [
      'You inspect canal machinery and are responsible for clearing unsafe traffic.',
      'You pilot a freight barge carrying workers and volatile industrial supplies.',
    ],
    tones: ['tactical', 'moral', 'chaotic'],
    abilityTags: ['metal', 'force', 'speed', 'structure', 'detection', 'leadership'],
    incident: 'A sabotaged lock gate traps three barges as water rises behind it, and one vessel is pinned beneath a loading crane.',
    duty: 'Your assigned task is to open an evacuation route before the retaining wall overtops.',
    pressures: [
      'The saboteur is still on the towpath carrying the only maintenance key.',
      'Workers on the pinned vessel report that its chemical drums have begun to shift.',
    ],
    optionA: 'commit your reserve to freeing the trapped vessel and its crew',
    optionB: 'open the gate before the rising water floods the district',
    stakes: 'Saving the people in immediate danger may remove the last chance to protect everyone downstream.',
  }),
  frame({
    id: 'rail-switch-failure',
    settingId: 'scadrial-rail-station',
    title: 'The Wrong Track',
    roles: [
      'You are the station inspector responsible for signing off on every departure.',
      'You supervise the platform crew and know which passengers need help evacuating.',
    ],
    tones: ['tactical', 'chaotic'],
    abilityTags: ['metal', 'force', 'speed', 'senses', 'time', 'leadership'],
    incident: 'A switching mechanism locks an incoming passenger train onto a platform already occupied by a disabled freight engine.',
    duty: 'You have less than two minutes to clear the collision path while preventing a crowd from surging onto the tracks.',
    pressures: [
      'The signal operator insists the switch shows green and refuses to authorize an evacuation.',
      'A second train is approaching, so diverting the first will transfer the danger to another platform.',
    ],
    optionA: 'use your power on the trains or switch and reveal what you can do',
    optionB: 'direct the evacuation and trust the ordinary brakes to stop the train',
    stakes: 'A failed intervention will turn a mechanical accident into a crowded-station disaster.',
  }),
  frame({
    id: 'roughs-water-ledger',
    settingId: 'scadrial-roughs-town',
    title: 'The Well Runs Dry',
    roles: [
      'You are the town constable, responsible for keeping the peace until outside help arrives.',
      'You maintain the town pump and know its recent failures were not accidental.',
    ],
    tones: ['moral', 'tactical'],
    abilityTags: ['detection', 'social', 'connection', 'resource', 'speed', 'strength'],
    incident: 'The town well stops producing water just as a captured raider offers proof that the mayor has been diverting the supply to a private cistern.',
    duty: 'You must keep the remaining water secure and prevent frightened residents from turning on one another.',
    pressures: [
      'The raider will reveal the cistern only if you release them before the mayor arrives.',
      'A sick family needs water now, but opening the reserve will expose how little remains.',
    ],
    optionA: 'preserve order and distribute the known reserve under the mayor’s rules',
    optionB: 'trust the prisoner and search for the hidden cistern before the town runs dry',
    stakes: 'Choosing the wrong source of authority could cost the town both its water and its remaining trust.',
  }),
  frame({
    id: 'estate-poisoned-toast',
    settingId: 'scadrial-noble-estate',
    title: 'A Toast No One Should Drink',
    roles: [
      'You manage security for the gathering and are expected to avoid embarrassing the host.',
      'You are a family representative whose presence is holding a fragile alliance together.',
    ],
    tones: ['moral', 'tactical'],
    abilityTags: ['senses', 'emotion', 'social', 'identity', 'stealth', 'detection'],
    incident: 'Minutes before a treaty toast, you discover evidence that one tray of wine is poisoned but cannot identify which faction arranged it.',
    duty: 'Your responsibility is to protect the guests without turning an uncertain suspicion into open conflict.',
    pressures: [
      'The only servant who handled every glass is trying to leave with a sealed letter.',
      'Canceling the toast will be read as a deliberate insult by both negotiating families.',
    ],
    optionA: 'intervene publicly and accept the political fallout',
    optionB: 'use your ability covertly to identify the poisoner before the toast begins',
    stakes: 'A quiet failure costs lives; a public accusation may restart a feud that endangers far more people.',
  }),
  frame({
    id: 'mine-pump-choice',
    settingId: 'scadrial-flooded-mine',
    title: 'Below the Flood Line',
    roles: [
      'You are the shift foreman and know the location of every crew below ground.',
      'You maintain the mine pumps and signed the report warning that the supports were failing.',
    ],
    tones: ['tactical', 'moral', 'chaotic'],
    abilityTags: ['strength', 'speed', 'breath', 'structure', 'metal', 'rescue'],
    incident: 'A collapsed gallery diverts floodwater toward two trapped crews while the main pump tears loose from its mounting.',
    duty: 'You must either restore drainage or reach the lower gallery before its remaining air pocket disappears.',
    pressures: [
      'Restarting the pump will draw water through the passage the rescue team needs to use.',
      'One trapped miner admits the collapse exposed an illegal shaft the owners will want buried.',
    ],
    optionA: 'spend your reserve stabilizing and restarting the pump',
    optionB: 'leave the upper workings to flood and enter the lower gallery for the trapped crew',
    stakes: 'The choice determines who receives the mine’s last usable margin of time.',
  }),
  frame({
    id: 'metalworks-ladle',
    settingId: 'scadrial-metalworks',
    title: 'The Ladle Breaks Free',
    roles: [
      'You are the floor foreman responsible for the crews beneath the casting line.',
      'You inspect lifting equipment and recognize the failure as deliberate sabotage.',
    ],
    tones: ['tactical', 'chaotic'],
    abilityTags: ['metal', 'force', 'temperature', 'speed', 'strength', 'structure'],
    incident: 'A suspended ladle of molten metal tears loose and swings toward a crowded casting floor as the furnace feed jams open.',
    duty: 'You must clear the workers and prevent the spill from reaching the fuel stores.',
    pressures: [
      'The emergency brake can stop the ladle, but engaging it will trap three workers on the upper catwalk.',
      'The saboteur is climbing toward the control room and can disable the remaining safety systems.',
    ],
    optionA: 'use your reserve to control the ladle and protect the workers beneath it',
    optionB: 'shut down the furnace feed before the entire building becomes impossible to save',
    stakes: 'Solving either failure leaves the other moving toward people who cannot escape unaided.',
  }),
  frame({
    id: 'terris-archive-storm',
    settingId: 'scadrial-terris-enclave',
    title: 'The Records Beneath the Snow',
    roles: [
      'You are an enclave steward responsible for shelter assignments during the storm.',
      'You are an archivist whose family has guarded the sealed records for generations.',
    ],
    tones: ['moral', 'balanced'],
    abilityTags: ['detection', 'connection', 'memory', 'speed', 'social', 'foresight'],
    incident: 'A fire alarm empties the archive into a blizzard while an escaped prisoner claims the evacuation was staged to destroy a sealed genealogy ledger.',
    duty: 'You are assigned to guide exposed residents to shelter before the mountain path vanishes under snow.',
    pressures: [
      'The prisoner knows a tunnel back into the archive but refuses to surrender the ledger afterward.',
      'An elder orders the archive sealed and insists the prisoner manufactured the alarm.',
    ],
    optionA: 'complete the evacuation and return the prisoner to custody',
    optionB: 'leave the shelter column and follow the prisoner to recover the ledger',
    stakes: 'The ledger may expose a betrayal within the enclave, but abandoning the evacuation puts real families at immediate risk.',
  }),
  frame({
    id: 'ash-road-caravan',
    settingId: 'scadrial-ash-plain',
    title: 'The Road Disappears',
    roles: [
      'You guide a supply caravan whose food is promised to an isolated settlement.',
      'You are the caravan medic and must decide which injuries can survive the journey.',
    ],
    tones: ['tactical', 'moral'],
    abilityTags: ['senses', 'speed', 'survival', 'breath', 'resource', 'leadership'],
    incident: 'An ashstorm erases the road after a wagon overturns, injuring several travelers and scattering the markers that point toward shelter.',
    duty: 'You must keep the caravan together long enough to locate either the shelter or the buried road.',
    pressures: [
      'A scout reports lights in the wrong direction and cannot tell whether they belong to rescuers or raiders.',
      'The damaged wagon carries the settlement’s medicine but is slowing everyone still able to walk.',
    ],
    optionA: 'spend your reserve finding and opening a route for the entire caravan',
    optionB: 'take the injured and medicine toward the uncertain lights before exposure worsens',
    stakes: 'The group you do not guide may lose both its supplies and its chance to reach cover.',
  }),
  frame({
    id: 'cavern-sealed-door',
    settingId: 'scadrial-cavern-complex',
    title: 'The Chamber Starts to Move',
    roles: [
      'You lead the survey team and promised every worker a safe route back to the surface.',
      'You are a historian who recognizes the machinery behind the sealed chamber.',
    ],
    tones: ['tactical', 'balanced'],
    abilityTags: ['detection', 'knowledge', 'metal', 'structure', 'speed', 'investiture'],
    incident: 'Opening an ancient door activates machinery that shifts the surrounding passages and seals half the expedition on the far side.',
    duty: 'You must restore a route to the trapped team before the chamber completes its next movement.',
    pressures: [
      'The controls also expose a cache of dangerous devices that another expedition is approaching.',
      'A trapped surveyor says the moving chamber is supporting the ceiling above the only exit.',
    ],
    optionA: 'reverse the mechanism immediately and recover the trapped expedition',
    optionB: 'hold the chamber in place long enough to secure or destroy what it has revealed',
    stakes: 'The same mechanism that can save your people may release a threat no one present understands.',
  }),
  frame({
    id: 'airship-mooring-failure',
    settingId: 'scadrial-airship-dock',
    title: 'The Last Mooring Line',
    roles: [
      'You supervise dock safety and are responsible for everyone working above the city.',
      'You are the airship quartermaster and know the cargo cannot survive a hard landing.',
    ],
    tones: ['tactical', 'chaotic'],
    abilityTags: ['flight', 'force', 'metal', 'speed', 'weight', 'leadership'],
    incident: 'A departing airship loses two mooring lines and swings toward the passenger platform while its lift machinery begins to fail.',
    duty: 'You must get the platform clear and prevent the ship from falling into the streets below.',
    pressures: [
      'The final mooring can hold the ship or the collapsing platform, but not both.',
      'The captain orders the cargo cut loose even though dockworkers are still beneath it.',
    ],
    optionA: 'secure the airship long enough to evacuate its passengers',
    optionB: 'protect the dock and force the damaged ship away from the populated district',
    stakes: 'Whichever structure loses support will carry people with it.',
  }),
  frame({
    id: 'warcamp-grain-panic',
    settingId: 'roshar-warcamp-market',
    title: 'The Grain Wagons Are Empty',
    roles: [
      'You oversee market security and must prevent soldiers from seizing civilian supplies.',
      'You represent refugee families who were promised food from today’s shipment.',
    ],
    tones: ['moral', 'chaotic'],
    abilityTags: ['emotion', 'leadership', 'connection', 'illusion', 'speed', 'resource'],
    incident: 'The army’s grain wagons arrive empty, and a forged notice sends hungry soldiers and refugees toward the same private storehouse.',
    duty: 'You must prevent a crush in the market while determining whether the storehouse actually contains food.',
    pressures: [
      'The merchant offers to open the doors only if the refugees are removed first.',
      'A runner has proof that an officer diverted the grain, but the crowd will not wait for an investigation.',
    ],
    optionA: 'control or redirect the crowd long enough to restore order',
    optionB: 'break into the storehouse and distribute whatever is inside',
    stakes: 'The method that feeds people tonight may decide which group controls the market tomorrow.',
  }),
  frame({
    id: 'shelter-shutter-line',
    settingId: 'roshar-highstorm-shelter',
    title: 'The Eastern Wall Opens',
    roles: [
      'You coordinate shelter assignments and know the building is over capacity.',
      'You are a mason responsible for the shutters that have begun to fail.',
    ],
    tones: ['tactical', 'moral', 'chaotic'],
    abilityTags: ['structure', 'strength', 'leadership', 'healing', 'adhesion', 'defense'],
    incident: 'The storm tears open an eastern shutter as a support beam cracks above the most crowded sleeping chamber.',
    duty: 'You must keep the breach from exposing hundreds of people while moving those beneath the failing roof.',
    pressures: [
      'Reinforcing the shutter will seal a late group outside with no other shelter in reach.',
      'The only intact bracing can support either the roof beam or the shutter assembly.',
    ],
    optionA: 'hold the breached wall and leave the crowded chamber to ordinary evacuation',
    optionB: 'abandon the wall long enough to stabilize the roof and clear the chamber',
    stakes: 'The storm will exploit whichever protection you leave incomplete.',
  }),
  frame({
    id: 'chasm-survey-rescue',
    settingId: 'roshar-shattered-chasm',
    title: 'Water Below, Soldiers Above',
    roles: [
      'You lead a chasm survey and are accountable for the bridgemen working below.',
      'You are the field medic treating workers injured by the first rockfall.',
    ],
    tones: ['tactical', 'moral'],
    abilityTags: ['flight', 'healing', 'strength', 'speed', 'structure', 'rescue'],
    incident: 'A flash flood traps the survey crew beneath an overhang while an enemy patrol begins cutting the rope ladder from above.',
    duty: 'You must create an escape route before the water reaches the injured workers.',
    pressures: [
      'Stopping the patrol will use the height and time needed to extract the least mobile worker.',
      'The survey leader orders you to recover the maps even if the wounded must be left behind.',
    ],
    optionA: 'commit your power to extracting the trapped crew through the rising water',
    optionB: 'reach the rim and secure the only route out before the patrol destroys it',
    stakes: 'The people below cannot survive without a route, but the route cannot survive without someone defending it.',
  }),
  frame({
    id: 'urithiru-water-order',
    settingId: 'roshar-urithiru',
    title: 'The Reservoir Order',
    roles: [
      'You coordinate water deliveries between tower districts with competing military commands.',
      'You are an engineer who discovered the reservoir fault and understands how quickly it is spreading.',
    ],
    tones: ['moral', 'balanced'],
    abilityTags: ['leadership', 'connection', 'structure', 'knowledge', 'healing', 'resource'],
    incident: 'A reservoir fault cuts water to three districts, and two highprinces issue contradictory orders for the remaining supply.',
    duty: 'You must keep the damaged system from emptying while directing water to people who cannot all be served.',
    pressures: [
      'The hospital district can survive the interruption only by taking the reserve promised to refugees.',
      'Repair crews can isolate the fault, but doing so will strand workers inside a flooding conduit.',
    ],
    optionA: 'obey the official priority order and preserve the chain of command',
    optionB: 'redirect the supply according to immediate need and accept responsibility for defying it',
    stakes: 'The allocation will cost lives now and establish who controls the tower’s necessities later.',
  }),
  frame({
    id: 'caravan-shelter-weight',
    settingId: 'roshar-stone-caravan',
    title: 'Too Far From Shelter',
    roles: [
      'You are the caravan quartermaster responsible for both pace and provisions.',
      'You lead the scouts and know the storm estimates are becoming unreliable.',
    ],
    tones: ['tactical', 'moral'],
    abilityTags: ['speed', 'flight', 'transportation', 'resource', 'survival', 'leadership'],
    incident: 'A broken axle leaves a family wagon behind just as scouts revise the highstorm’s arrival to an hour earlier.',
    duty: 'You must get the caravan to distant shelter without abandoning people who cannot keep pace on foot.',
    pressures: [
      'Repairing the wagon requires unloading the water barrels needed after the storm.',
      'A nearer rock formation may provide cover, but the route to it has not been surveyed.',
    ],
    optionA: 'spend your reserve moving the stranded family and critical supplies toward known shelter',
    optionB: 'turn the whole caravan toward the uncertain refuge and keep everyone together',
    stakes: 'A faster rescue may save one wagon while leaving the larger caravan without what it needs to survive.',
  }),
  frame({
    id: 'port-harbor-chain',
    settingId: 'roshar-coastal-port',
    title: 'The Harbor Chain Holds',
    roles: [
      'You direct dock evacuations and know which warehouses still contain workers.',
      'You are the harbor engineer responsible for the chain protecting the inner docks.',
    ],
    tones: ['tactical', 'chaotic'],
    abilityTags: ['water', 'storm', 'force', 'flight', 'structure', 'rescue'],
    incident: 'A disabled ship drags toward the harbor chain while a storm surge floods the warehouses behind the seawall.',
    duty: 'You must keep the ship from breaking into the crowded inner harbor while evacuating workers cut off by water.',
    pressures: [
      'Releasing the chain will save the ship’s crew but send the vessel into the smaller boats sheltering inside.',
      'The warehouse foreman refuses to leave without ledgers that could prove years of smuggling.',
    ],
    optionA: 'hold or redirect the ship before it reaches the chain',
    optionB: 'leave the harbor defense to fail and use your reserve extracting the trapped workers',
    stakes: 'The harbor and the people at its edge cannot both receive your full effort.',
  }),
  frame({
    id: 'oathgate-quarantine',
    settingId: 'roshar-oathgate',
    title: 'Do Not Open the Gate',
    roles: [
      'You command the platform guard and must enforce travel restrictions from both kingdoms.',
      'You coordinate civilian transfers and recognize families waiting on the far side.',
    ],
    tones: ['moral', 'tactical'],
    abilityTags: ['transportation', 'connection', 'healing', 'detection', 'leadership', 'investiture'],
    incident: 'A quarantine order arrives moments before the Oathgate is scheduled to bring through refugees fleeing an advancing army.',
    duty: 'You must decide whether the platform remains sealed while healers determine whether the reported illness is real.',
    pressures: [
      'The order bears a valid seal, but its messenger cannot answer basic questions about the outbreak.',
      'Opening the gate once may exhaust the mechanism’s available Stormlight and prevent a later military evacuation.',
    ],
    optionA: 'keep the gate sealed and obey the quarantine',
    optionB: 'open it for the waiting refugees before the enemy reaches them',
    stakes: 'Either choice may carry a disaster across the gate—or leave innocent people to one already underway.',
  }),
  frame({
    id: 'gemstone-lift-light',
    settingId: 'roshar-gemstone-mine',
    title: 'The Lift Stops Below',
    roles: [
      'You are the mine overseer responsible for the workers on the lowest shift.',
      'You maintain the lift and know its counterweight chamber is close to collapse.',
    ],
    tones: ['tactical', 'moral'],
    abilityTags: ['structure', 'strength', 'light', 'healing', 'speed', 'rescue'],
    incident: 'The main lift jams below the collapse line with injured miners aboard while infused spheres throughout the lower works begin going dark.',
    duty: 'You must restore a path upward before the trapped workers lose both light and breathable air.',
    pressures: [
      'Restarting the lift may collapse the shaft onto a second crew climbing the maintenance ladders.',
      'The mine owner orders the remaining light reserved for recovering a newly exposed gemheart seam.',
    ],
    optionA: 'use your reserve to raise or stabilize the lift',
    optionB: 'descend through the maintenance shaft and guide the workers out individually',
    stakes: 'The safest route for one group directly endangers the other route already in use.',
  }),
  frame({
    id: 'ruin-warning-stone',
    settingId: 'roshar-ancient-ruin',
    title: 'The Warning Under the Crem',
    roles: [
      'You guard the expedition and promised to withdraw at the first sign of danger.',
      'You are the scholar who identified the ruin and need one more hour to understand it.',
    ],
    tones: ['tactical', 'balanced'],
    abilityTags: ['knowledge', 'illusion', 'detection', 'transformation', 'structure', 'investiture'],
    incident: 'Removing crem from a carved doorway awakens a pattern of light beneath the floor as another expedition closes in on the site.',
    duty: 'You must determine whether the ruin is a warning, a weapon, or a shelter before either group activates it blindly.',
    pressures: [
      'The rival expedition has an injured guide and offers information in exchange for entering first.',
      'The pattern points toward an underground chamber while the next storm is already visible.',
    ],
    optionA: 'seal the doorway and withdraw everyone before the mechanism completes',
    optionB: 'enter the chamber now and learn what the warning was built to contain',
    stakes: 'Retreat preserves lives but leaves the site to rivals; entering may awaken the danger the warning describes.',
  }),
  frame({
    id: 'shadesmar-bead-debt',
    settingId: 'roshar-shadesmar-dock',
    title: 'The Cargo Has a Mind',
    roles: [
      'You negotiate passage for travelers stranded far from a stable perpendicularity.',
      'You supervise the dock and are responsible for agreements made with local spren.',
    ],
    tones: ['moral', 'balanced'],
    abilityTags: ['connection', 'transportation', 'identity', 'illusion', 'social', 'realmatic'],
    incident: 'A sealed cargo manifests as a terrified sapient spren just as the ship carrying your stranded travelers prepares to depart.',
    duty: 'You must secure passage for the travelers without violating the dock’s binding trade agreements.',
    pressures: [
      'The captain claims the spren freely accepted transport, but its memories have clearly been altered.',
      'Freeing the spren voids the fare contract and leaves the travelers without food for another week.',
    ],
    optionA: 'honor the contract and get the travelers safely aboard',
    optionB: 'free the captive spren and sacrifice the group’s only arranged passage',
    stakes: 'Your decision defines whether survival justifies treating a thinking being as cargo.',
  }),
];

export function validateScenarioFrames(frames, settings) {
  const settingIds = new Set(settings.map((setting) => setting.id));
  const ids = new Set();
  const errors = [];
  const requiredText = ['id', 'settingId', 'title', 'incident', 'duty', 'optionA', 'optionB', 'stakes'];

  frames.forEach((candidate, index) => {
    const label = candidate.id || `frame at index ${index}`;
    requiredText.forEach((field) => {
      if (typeof candidate[field] !== 'string' || !candidate[field].trim()) {
        errors.push(`${label}: missing ${field}`);
      }
    });
    if (ids.has(candidate.id)) errors.push(`${label}: duplicate id`);
    ids.add(candidate.id);
    if (!settingIds.has(candidate.settingId)) errors.push(`${label}: unknown setting ${candidate.settingId}`);
    if (!Array.isArray(candidate.roles) || candidate.roles.length < 2) errors.push(`${label}: needs two roles`);
    if (!Array.isArray(candidate.tones) || !candidate.tones.length) errors.push(`${label}: needs tones`);
    if (!Array.isArray(candidate.abilityTags) || !candidate.abilityTags.length) errors.push(`${label}: needs ability tags`);
    if (!Array.isArray(candidate.pressures) || candidate.pressures.length < 2) errors.push(`${label}: needs two pressures`);
  });

  return errors;
}
